import { DeviceType } from '@graphql/generated';
import { useMemo } from 'react';
import { useApiMetadata } from './useApiMetadata';
import { useClientReference } from './useClientReference';
import { PricingApiCallSource } from '@apps/stays/types';
import { usePricingCallSource } from './usePricingCallSource';

export type BaseSearchMetadata = Readonly<{
  eventId: string | undefined;
  anonymousId: string | undefined;
  deviceType: DeviceType | undefined;
  userCountryCode: string | undefined;
}>;

export type SearchMetadataWithCallSource = Readonly<
  BaseSearchMetadata & {
    callSource: PricingApiCallSource | undefined;
  }
>;

export const useBaseSearchMetadata = () => {
  const { deviceType, anonymousId, eventId, userCountryCode } = useApiMetadata();
  const { loadingClientReference } = useClientReference();

  const baseMetadata: BaseSearchMetadata = useMemo(
    () => ({
      eventId,
      deviceType,
      anonymousId,
      userCountryCode,
    }),
    [eventId, anonymousId, deviceType, userCountryCode],
  );

  return {
    baseMetadata,
    isPreparingMetadata: loadingClientReference,
  };
};

/**
 * Hook for generating metadata for queries that support callSource tracking.
 * Used by: travelPlacesPricingAndBookingDetails, travelPricingsForAccommodationByPropertyId
 */
export const useSearchAccommodationsQueryMetadataPayload = (
  callSource?: PricingApiCallSource,
): {
  isPreparingSearchMetadataPayload: boolean;
  searchMetadata: SearchMetadataWithCallSource | null;
} => {
  const { isPreparingMetadata, baseMetadata } = useBaseSearchMetadata();
  const autoDetectedCallSource = usePricingCallSource();
  const effectiveCallSource = callSource ?? autoDetectedCallSource;

  return useMemo(() => {
    return {
      isPreparingSearchMetadataPayload: isPreparingMetadata,
      searchMetadata: isPreparingMetadata
        ? null
        : {
            ...baseMetadata,
            callSource: effectiveCallSource,
          },
    };
  }, [isPreparingMetadata, baseMetadata, effectiveCallSource]);
};

/**
 * Hook for generating metadata for extended accommodation search queries.
 * Used by: searchExtendedAccommodationProperties, searchExtendedAccommodationPropertiesV2
 */
export const useExtendedAccommodationSearchMetadata = (): {
  isPreparingSearchMetadata: boolean;
  searchMetadata: BaseSearchMetadata | null;
} => {
  const { isPreparingMetadata, baseMetadata } = useBaseSearchMetadata();

  return useMemo(() => {
    return {
      isPreparingSearchMetadata: isPreparingMetadata,
      searchMetadata: isPreparingMetadata ? null : baseMetadata,
    };
  }, [isPreparingMetadata, baseMetadata]);
};
